// Elasticsearch emits Long.MIN_VALUE (ascending sorts) or Long.MAX_VALUE
// (descending sorts) as the `_sort` key when a sort field is missing/null.
// These values exceed Number.MAX_SAFE_INTEGER, so the default JSON.parse rounds
// them and the round-tripped `search_after` cursor breaks pagination. We swap
// each sentinel for a string marker on the way in and restore the exact literal
// on the way out, so it travels safely through every intermediate layer (Redux
// state, thunks, request body) as an opaque string.
export const I64_MIN_LITERAL = '-9223372036854775808';
export const I64_MIN_MARKER = '__I64_MIN__';
export const I64_MAX_LITERAL = '9223372036854775807';
export const I64_MAX_MARKER = '__I64_MAX__';

interface Sentinel {
  literal: string;
  marker: string;
  // The literal parsed to its nearest double — what an already-parsed JSON
  // number value is compared against.
  number: number;
}

const SENTINELS: Sentinel[] = [
  {
    literal: I64_MIN_LITERAL,
    marker: I64_MIN_MARKER,
    number: Number(I64_MIN_LITERAL),
  },
  {
    literal: I64_MAX_LITERAL,
    marker: I64_MAX_MARKER,
    number: Number(I64_MAX_LITERAL),
  },
];

// We match on the parsed double rather than the ES2025 reviver `context.source`
// (exact source text), which isn't supported on older browsers. This stays
// unambiguous for the int64 domain: no real int64 value maps to these doubles
// except the sentinels themselves. Long.MIN_VALUE is exactly -2^63, and the
// rounded Long.MAX_VALUE double (2^63) is out of int64 range, so nothing else
// can emit it.
const sentinelReviver = (_key: string, value: unknown): unknown => {
  if (typeof value !== 'number') {
    return value;
  }
  for (const sentinel of SENTINELS) {
    if (value === sentinel.number) {
      return sentinel.marker;
    }
  }
  return value;
};

// Way in: replaces Axios's default response parse so the reviver runs. Mirrors
// Axios default behavior by only parsing strings and swallowing parse errors.
export const parseI64SentinelResponse = (data: unknown): unknown => {
  if (typeof data !== 'string') {
    return data;
  }
  try {
    return JSON.parse(data, sentinelReviver);
  } catch {
    return data;
  }
};

// Way out: composes after Axios's default stringify and unquotes each marker
// back into its exact integer literal.
export const encodeI64SentinelRequest = (data: unknown): unknown => {
  if (typeof data !== 'string') {
    return data;
  }
  let result = data;
  for (const sentinel of SENTINELS) {
    if (result.includes(sentinel.marker)) {
      result = result.replaceAll(`"${sentinel.marker}"`, sentinel.literal);
    }
  }
  return result;
};
